import {gql} from '@apollo/client'
import React, {useCallback} from 'react'
import {css} from 'styled-components'
import {CardFilterFieldsBase} from '../../common/cards/synced-card-feed-filter-query'
import {CARD_CANCEL_LIMIT} from '../../common/types/constants'
import {ButtonGroup} from '../../components/form'
import {Button} from '../../components/library/buttons'
import {TextInput} from '../../components/library/form'
import {Modal, ModalDescription} from '../../components/library/modal/modal'
import {EnvironmentNode} from '../../environment'
import {FlowConfig} from '../../hooks/use-action-flow-config'
import {pruneObject} from '../../lib/helpers'
import {cardTemplateVersionName} from '../card-builder/helpers'
import {ErrorInfo, WarnInfo} from './error'
import {useCardListCountQuery} from './generated/review'

type CancellationReviewModalProps = {
  environment: EnvironmentNode
  criteria: CardFilterFieldsBase
  actionFlowConfigs: FlowConfig[]
  onCancel: () => void
  onComplete: (criteria: CardFilterFieldsBase) => void
  isPerformingCancel: boolean
}
export const CancellationReviewModal = ({
  environment,
  actionFlowConfigs,
  criteria,
  isPerformingCancel,
  onCancel,
  onComplete
}: CancellationReviewModalProps) => {
  const currentFlowConfig = criteria.flowConfigId
    ? actionFlowConfigs.find(c => c.id === criteria.flowConfigId)
    : undefined

  const currentCardTemplate =
    currentFlowConfig && criteria.cardTemplateId
      ? currentFlowConfig.currentVersion.config.cardTemplates?.[
          criteria.cardTemplateId
        ]
      : undefined

  const {data, loading, error} = useCardListCountQuery({
    variables: {
      environmentId: environment.id,
      // strip out empty strings so as not to confuse the backend
      criteria: {
        ...pruneObject(criteria, [null, undefined, '']),

        // only count cards that are still active
        status: ['active', 'snoozed', 'embargoed']
      }
    }
  })
  const cancellationCount = data?.cardList?.totalCount ?? -1 // if we timeout doing the query then we don't know the count

  const performCancellation = useCallback(
    (c: CardFilterFieldsBase) => {
      onComplete(pruneObject(c, [null, undefined, '']))
    },
    [onComplete]
  )

  return (
    <Modal
      loading={loading}
      title="Cancel cards"
      handleClose={onCancel}
      style={{width: '520px'}}
    >
      <ModalDescription>
        Check details before confirming cancellation.
      </ModalDescription>

      <DisplayValue
        label="Number of cards to be cancelled"
        // space so the label stays "up" even when value is empty
        value={
          cancellationCount > 0 ? cancellationCount?.toLocaleString() : ' '
        }
        error={cancellationCount <= 0}
      />

      {cancellationCount < 0 || error ? (
        <ErrorInfo>
          <>Unable to retrieve the card count.</>
        </ErrorInfo>
      ) : null}

      {cancellationCount === 0 ? (
        <ErrorInfo>No cards match the cancellation criteria.</ErrorInfo>
      ) : null}

      {cancellationCount === CARD_CANCEL_LIMIT ? (
        <WarnInfo>
          Maximum number of cards in one cancellation is{' '}
          {CARD_CANCEL_LIMIT.toLocaleString()}. To cancel more run another
          cancellation after this one completes.
        </WarnInfo>
      ) : null}

      <h4>Cancellation criteria</h4>
      {currentCardTemplate && (
        <DisplayValue
          label="Card"
          value={`${cardTemplateVersionName(
            currentCardTemplate.metadata.cardName
          )} (ID: ${criteria.cardTemplateId})`}
        />
      )}

      {currentFlowConfig && (
        <DisplayValue
          label="Action Flow"
          value={`${currentFlowConfig.name || 'Unnamed'} (ID: ${
            criteria.flowConfigId
          })`}
        />
      )}

      {criteria.cardTemplateVersion && (
        <DisplayValue
          label="Card template version"
          value={`Version ${criteria.cardTemplateVersion}`}
        />
      )}

      {criteria.userId && (
        <DisplayValue label="Customer ID" value={criteria.userId} />
      )}

      {criteria.cardInstanceId && (
        <DisplayValue
          label="Card instance ID"
          value={criteria.cardInstanceId}
        />
      )}

      {criteria.flowInvocationId && (
        <DisplayValue
          label="Action Flow invocation ID"
          value={criteria.flowInvocationId}
        />
      )}

      {criteria.flowInstanceId && (
        <DisplayValue
          label="Action Flow instance ID"
          value={criteria.flowInstanceId}
        />
      )}

      <ButtonGroup>
        <Button
          type="submit"
          size="large"
          appearance="warning"
          disabled={!cancellationCount || isPerformingCancel}
          title={!cancellationCount ? 'No matching cards' : ''}
          onClick={() => {
            performCancellation(criteria)
          }}
        >
          Cancel cards now
        </Button>
        <Button onClick={onCancel} appearance="secondary" size="large">
          Back to criteria
        </Button>
      </ButtonGroup>
    </Modal>
  )
}

type DisplayValueProps = {
  label: string
  value: string | number
  error?: boolean
}
const DisplayValue = ({label, value, error}: DisplayValueProps) => (
  <TextInput
    readOnly={true}
    onChange={() => null}
    // @ts-ignore
    css={
      error
        ? css`
            border-color: ${p => p.theme.red};
          `
        : undefined
    }
    label={label}
    value={String(value)}
    style={{marginBottom: '20px'}}
    data-testid={label}
  />
)

gql`
  query CardListCount($environmentId: String!, $criteria: JSON) {
    cardList(condition: {environmentId: $environmentId}, criteria: $criteria) {
      totalCount
    }
  }
`
